import { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import BackOfficeAPIClient from '../../../../clients/BackofficeAPIClient'
import { Company } from '../../../../types/Company'
import { People } from '../../../../types/People'
import { SignatureDataResponse } from '../Types/Interface/SignatureDataResponse'
import { stringifyRelances } from '../Types/Interface/Relance'
import { useCreditsManagement } from './useCreditsManagement'
import { useConnectionsManagement } from './useConnectionsManagement'
import { useAlertsManagement } from './useAlertsManagement'
import { useRelancesManagement } from './useRelancesManagement'

interface UseSignatureFormReturn {
    // State global
    loading: boolean
    hasChanges: boolean
    setHasChanges: (value: boolean) => void
    showCancelModal: boolean
    setShowCancelModal: (value: boolean) => void
    companies: Company[]
    users: People[]
    selectedCompany: Company | null
    setSelectedCompany: (company: Company | null) => void

    contreSignatureAutomatique: boolean
    setContreSignatureAutomatique: (v: boolean) => void

    // Actions globales
    handleSave: () => Promise<void>
    handleCancel: () => void
    confirmCancel: () => void

    // Hooks spécifiques
    creditsHook: ReturnType<typeof useCreditsManagement>
    connectionsHook: ReturnType<typeof useConnectionsManagement>
    alertsHook: ReturnType<typeof useAlertsManagement>
    relancesHook: ReturnType<typeof useRelancesManagement>
}

export function useSignatureForm(appName: string, history: ReturnType<typeof useHistory>): UseSignatureFormReturn {
    const backofficeApiClient = new BackOfficeAPIClient()

    // State global
    const [loading, setLoading] = useState<boolean>(true)
    const [hasChanges, setHasChanges] = useState<boolean>(false)
    const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
    const [companies, setCompanies] = useState<Company[]>([])
    const [users, setUsers] = useState<People[]>([])
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
    const [contreSignatureAutomatique, setContreSignatureAutomatiqueState] = useState<boolean>(false)

    const setContreSignatureAutomatique = (v: boolean) => {
        setContreSignatureAutomatiqueState(v)
        setHasChanges(true)
    }

    // Custom hooks pour chaque section
    const creditsHook = useCreditsManagement(setHasChanges)
    const connectionsHook = useConnectionsManagement(setHasChanges)
    const alertsHook = useAlertsManagement(setHasChanges)
    const relancesHook = useRelancesManagement(setHasChanges)

    // Chargement initial des données (companies et users)
    const fetchInitialData = useCallback(async () => {
        setLoading(true)
        try {
            const [companiesData, usersData] = await Promise.all([
                backofficeApiClient.get<Company[]>(`/interfaces/signatures/${appName}/societes/detailed`),
                backofficeApiClient.get<People[]>(`/interfaces/signatures/utilisateurs`)
            ]) as [Company[], People[]];

            setCompanies(companiesData)
            setUsers(usersData.map(u => ({ ...u, FullName: `${u.Lastname} ${u.Firstname.charAt(0).toUpperCase() + u.Firstname.slice(1).toLowerCase()}` })))
        } catch (error) {
            console.error('Erreur lors du chargement des données:', error)
            alert('Erreur lors du chargement des données')
        } finally {
            setLoading(false)
        }
    }, [appName])

    // Chargement des données de signature pour une société
    const fetchSignatureData = useCallback(async () => {
        if (!selectedCompany) return

        try {
            const signatureData = await backofficeApiClient.get<SignatureDataResponse>(
                `/interfaces/signatures/${appName}/societes/${selectedCompany.IDPlaque}`
            )

            if (signatureData) {
                // Charger les données dans les hooks
                creditsHook.loadCredits(signatureData.contrats || [])
                connectionsHook.loadConnections(selectedCompany.Sites, signatureData.connexions)
                alertsHook.loadAlertes(
                    signatureData.signature?.SeuilAlerte || 150,
                    signatureData.alertes || []
                )
                relancesHook.loadRelances(signatureData.signature?.Relances)
                setContreSignatureAutomatiqueState(signatureData.signature?.ContreSignatureAutomatique || false)
            } else {
                // Initialiser avec des valeurs par défaut
                creditsHook.resetCredits()
                connectionsHook.loadConnections(selectedCompany.Sites)
                alertsHook.resetAlertes()
                relancesHook.resetRelances()
                setContreSignatureAutomatiqueState(false)
            }
        } catch (error) {
            console.error('Erreur lors du chargement de la signature:', error)
            // Initialiser avec des valeurs par défaut en cas d'erreur
            creditsHook.resetCredits()
            connectionsHook.loadConnections(selectedCompany.Sites)
            alertsHook.resetAlertes()
            relancesHook.resetRelances()
        }
    }, [selectedCompany, appName])

    // Charger les données initiales au montage
    useEffect(() => {
        fetchInitialData()
    }, [fetchInitialData])

    // Charger les données de signature quand une société est sélectionnée
    useEffect(() => {
        if (selectedCompany) {
            fetchSignatureData()
        }
    }, [selectedCompany, fetchSignatureData])

    // Sauvegarde
    const handleSave = async () => {
        if (!selectedCompany) {
            alert('Veuillez sélectionner une société')
            return
        }

        if (creditsHook.credits.length === 0) {
            alert('Veuillez ajouter au moins un contrat')
            return
        }

        // Valider les connexions actives
        const invalidConnections = connectionsHook.connections.filter(c =>
            c.EstActif && c.IDPeopleRepresentant && c.Token && c.TestStatus !== 'active'
        )
        if (invalidConnections.length > 0) {
            alert('Certaines connexions actives ont un représentant et un token mais n\'ont pas été testées avec succès')
            return
        }

        const data = {
            IDPlaque: selectedCompany.IDPlaque,
            NomApp: appName,
            SeuilAlerte: alertsHook.seuilAlerte,
            Relances: stringifyRelances(relancesHook.relances),
            Status: 'ACTIVE',
            ContreSignatureAutomatique: contreSignatureAutomatique,
            contrats: creditsHook.credits,
            deletedContrats: creditsHook.deletedContrats || [],
            connexions: connectionsHook.connections.filter(c => c.IDPeopleRepresentant && c.Token).map(c => {
                const { OriginalToken, IsTokenModified, ...rest } = c
                return {
                    ...rest,
                    EstActif: c.EstActif !== undefined ? c.EstActif : true
                }
            }),
            alertes: alertsHook.personnesAlerter,
            deletedAlertes: alertsHook.deletedAlertes || []
        }

        try {
            await backofficeApiClient.post(
                `/interfaces/signatures/${appName}/societes`,
                data
            )
            setHasChanges(false)
            history.push(`/interfaces/signatures/${appName}`)
        } catch (error: any) {
            console.error('Erreur lors de l\'enregistrement:', error)
            const errorMessage = error?.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement'
            alert(errorMessage)
        }
    }

    // Annulation
    const handleCancel = () => {
        if (hasChanges) {
            setShowCancelModal(true)
        } else {
            history.push(`/interfaces/signatures/${appName}`)
        }
    }

    const confirmCancel = () => {
        setShowCancelModal(false)
        history.push(`/interfaces/signatures/${appName}`)
    }

    return {
        // State global
        loading,
        hasChanges,
        setHasChanges,
        showCancelModal,
        setShowCancelModal,
        companies,
        users,
        selectedCompany,
        setSelectedCompany,

        contreSignatureAutomatique,
        setContreSignatureAutomatique,

        // Actions globales
        handleSave,
        handleCancel,
        confirmCancel,

        // Hooks spécifiques
        creditsHook,
        connectionsHook,
        alertsHook,
        relancesHook,
    }
}
